import { useState, useEffect } from 'react';
import { servicesAPI } from '../../api/services';
import { Edit2, Trash2, Plus, X, Save } from 'lucide-react';
import Loader from '../common/Loader';

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    category: 'web',
    priceStartingFrom: '',
    deliveryTime: '',
    features: [''],
    examples: []
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await servicesAPI.getAdminAll();
      setServices(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await servicesAPI.update(editingId, formData);
      } else {
        await servicesAPI.create(formData);
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadServices();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement du service');
    }
  };

  const handleEdit = (service) => {
    setEditingId(service._id);
    setFormData({
      title: service.title,
      description: service.description,
      icon: service.icon || '',
      color: service.color || '#3B82F6',
      category: service.category || 'web',
      priceStartingFrom: service.priceStartingFrom || '',
      deliveryTime: service.deliveryTime || '',
      features: service.features && service.features.length > 0 ? [...service.features] : [''],
      examples: service.examples || []
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce service ?')) {
      return;
    }
    
    try {
      setServices(prevServices => prevServices.filter(service => service._id !== id));
      await servicesAPI.delete(id);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du service');
      loadServices();
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      category: 'web',
      priceStartingFrom: '',
      deliveryTime: '',
      features: [''],
      examples: []
    });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...formData.features, ''] });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader />
    </div>
  );

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6 shadow-2xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-gray-700/50">
        <h2 className="text-2xl font-bold text-white">
          Liste des Services
        </h2>
        <button 
          onClick={() => setShowForm(true)} 
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95 whitespace-nowrap"
        >
          <Plus size={20} />
          Nouveau Service
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700/50 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-700/50 sticky top-0 bg-gray-900 z-10">
              <h3 className="text-xl font-bold text-white">
                {editingId ? 'Modifier' : 'Nouveau'} Service
              </h3>
              <button 
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }} 
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors duration-200"
              >
                <X size={20} className="text-gray-300" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Titre *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                  >
                    <option value="web">Site Web</option>
                    <option value="marketing">Marketing</option>
                    <option value="print">Impression</option>
                    <option value="consulting">Consulting</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                
                <div className="space-y-2 col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    required
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Icône (emoji)
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    placeholder="Ex: 🚀"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Couleur
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-12 h-12 cursor-pointer rounded-lg border border-gray-600"
                    />
                    <span className="text-gray-300 text-sm">
                      {formData.color}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Prix à partir de (€)
                  </label>
                  <input
                    type="number"
                    value={formData.priceStartingFrom}
                    onChange={(e) => setFormData({ ...formData, priceStartingFrom: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    min="0"
                    step="0.01"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Délai de livraison
                  </label>
                  <input
                    type="text"
                    value={formData.deliveryTime}
                    onChange={(e) => setFormData({ ...formData, deliveryTime: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                    placeholder="Ex: 2-3 semaines"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Fonctionnalités
                </label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      className="flex-1 px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-300"
                      placeholder="Fonctionnalité"
                    />
                    {formData.features.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => {
                          const newFeatures = formData.features.filter((_, i) => i !== index);
                          setFormData({ ...formData, features: newFeatures });
                        }}
                        className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-md transition-colors duration-200"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={addFeature}
                  className="px-4 py-2 border border-gray-600 hover:border-blue-400 text-gray-300 hover:text-blue-400 font-medium rounded-lg transition-all duration-300"
                >
                  + Ajouter une fonctionnalité
                </button>
              </div>
              
              <div className="flex gap-3 pt-4 border-t border-gray-700/50">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    resetForm();
                  }} 
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium rounded-lg transition-all duration-300 active:scale-95"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20 active:scale-95"
                >
                  <Save size={20} />
                  {editingId ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {services.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p>Aucun service disponible. Cliquez sur "Nouveau Service" pour en créer un.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {services.map((service) => (
            <div 
              key={service._id} 
              className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 animate-fadeIn"
            >
              <div 
                className="p-4"
                style={{ backgroundColor: `${service.color}20` }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    {service.icon && (
                      <div 
                        className="flex items-center justify-center w-10 h-10 rounded-lg text-xl"
                        style={{ color: service.color }}
                      >
                        {service.icon}
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-white">
                      {service.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(service);
                      }} 
                      className="p-2 bg-gray-700/50 hover:bg-gray-600 text-gray-300 rounded-lg transition-all duration-200 hover:scale-105" 
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(service._id);
                      }} 
                      className="p-2 bg-gray-700/50 hover:bg-red-500/20 text-gray-300 hover:text-red-400 rounded-lg transition-all duration-200 hover:scale-105" 
                      title="Supprimer définitivement"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-5">
                <p className="text-gray-400 text-sm mb-4">
                  {service.description}
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">Catégorie:</span>
                    <span className="text-sm text-white font-medium px-3 py-1 bg-gray-700/50 rounded-full">
                      {service.category}
                    </span>
                  </div>
                  
                  {service.priceStartingFrom && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">Prix:</span>
                      <span className="text-sm font-bold text-green-400">
                        À partir de {service.priceStartingFrom}€
                      </span>
                    </div>
                  )}
                  
                  {service.deliveryTime && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-300">Délai:</span>
                      <span className="text-sm text-blue-400">
                        {service.deliveryTime}
                      </span>
                    </div>
                  )}
                </div>
                
                {service.features && service.features.length > 0 && (
                  <div className="space-y-2 pt-4 border-t border-gray-700/50">
                    <h4 className="text-sm font-medium text-gray-300">
                      Fonctionnalités:
                    </h4>
                    <ul className="space-y-1">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="text-gray-400 text-sm flex items-start gap-1">
                          <span className="text-blue-400 mt-1">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                      {service.features.length > 3 && (
                        <li className="text-gray-500 text-sm">
                          ... et {service.features.length - 3} autres
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}